import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DollarSign, TrendingUp, AlertTriangle } from "lucide-react";

export default function FinancialAid() {
  const { data: aidRecords = [], isLoading } = useQuery({
    queryKey: ["financial-aid-tracking"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("financial_aid_tracking")
        .select(`
          *,
          athletes (name, sport)
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const stats = {
    total: aidRecords.length,
    totalScholarships: aidRecords.reduce((sum, r) => sum + Number(r.scholarship_amount || 0), 0),
    totalNIL: aidRecords.reduce((sum, r) => sum + Number(r.total_nil_compensation || 0), 0),
    compliant: aidRecords.filter((r) => r.compliance_status === "compliant").length,
    warning: aidRecords.filter((r) => r.compliance_status === "warning").length,
    overLimit: aidRecords.filter((r) => r.compliance_status === "over_limit").length,
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "compliant": return "default";
      case "warning": return "secondary";
      case "over_limit": return "destructive";
      case "under_review": return "outline";
      default: return "outline";
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <DollarSign className="h-8 w-8 text-primary" />
            Financial Aid & NIL Compensation
          </h1>
          <p className="text-muted-foreground mt-2">
            Track athletic scholarships, financial aid, and NIL compensation for NCAA compliance
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Scholarships</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${(stats.totalScholarships / 1000).toFixed(0)}K</div>
              <p className="text-xs text-muted-foreground">Athletic aid distributed</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total NIL Compensation</CardTitle>
              <TrendingUp className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">${(stats.totalNIL / 1000).toFixed(0)}K</div>
              <p className="text-xs text-muted-foreground">NIL earnings tracked</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Compliant Athletes</CardTitle>
              <TrendingUp className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.compliant}</div>
              <p className="text-xs text-muted-foreground">Within NCAA limits</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Need Attention</CardTitle>
              <AlertTriangle className="h-4 w-4 text-destructive" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-destructive">{stats.warning + stats.overLimit}</div>
              <p className="text-xs text-muted-foreground">Warnings or over limit</p>
            </CardContent>
          </Card>
        </div>

        {/* Financial Aid Table */}
        <Card>
          <CardHeader>
            <CardTitle>Athlete Financial Aid Records</CardTitle>
            <CardDescription>Scholarships, aid, and NIL compensation tracking</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-12 text-muted-foreground">Loading financial aid data...</div>
            ) : aidRecords.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                No financial aid records found. Demo data may not be loaded.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Athlete</TableHead>
                    <TableHead>Sport</TableHead>
                    <TableHead>Academic Year</TableHead>
                    <TableHead>Scholarship</TableHead>
                    <TableHead>Other Aid</TableHead>
                    <TableHead>NIL Comp</TableHead>
                    <TableHead>Total Comp</TableHead>
                    <TableHead>NCAA Limit</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {aidRecords.map((record) => (
                    <TableRow key={record.id}>
                      <TableCell className="font-medium">{record.athletes?.name}</TableCell>
                      <TableCell>{record.athletes?.sport}</TableCell>
                      <TableCell>{record.academic_year}</TableCell>
                      <TableCell>${Number(record.scholarship_amount || 0).toLocaleString()}</TableCell>
                      <TableCell>${Number(record.other_aid_amount || 0).toLocaleString()}</TableCell>
                      <TableCell className="text-green-600 font-semibold">
                        ${Number(record.total_nil_compensation || 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="font-semibold">
                        ${Number(record.total_compensation || 0).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {record.ncaa_limit_applicable 
                          ? `$${Number(record.ncaa_limit_applicable).toLocaleString()}` 
                          : 'N/A'
                        }
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusColor(record.compliance_status)}>
                          {record.compliance_status.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
